import { Injectable } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { MODULES } from 'src/app/core/config/module-codes';
import { OrganizationService } from 'src/app/data/service/organization.service';
import { UpgradeModalService } from '../upgrade-modal/upgrade-modal.service';
import { InboxAutomatorModalComponent, InboxAutomatorModalOptions, InboxAutomatorModalResult } from './inbox-automator-modal.component';
import { InboxAutomatorService } from './inbox-automator.service';

/**
 * SD-1229: opens Inbox Automator for a mailbox:
 *   this.inboxAutomatorModal.open({ email, provider, justConnected: true })
 *
 * The mailbox-connected message reaches every listener on the page -- the Emails page and
 * Settings > Integrations both listen for it, and the latter's listener outlives its page --
 * so a second open for the same mailbox while one is showing returns the first.
 *
 * "Upgrade to activate" swaps this modal for the upgrade modal rather than stacking the two,
 * and brings it back when the upgrade modal closes -- with Activate ready if a plan was bought.
 */
@Injectable({ providedIn: 'root' })
export class InboxAutomatorModalService {
  private open$ = new Map<string, Promise<InboxAutomatorModalResult>>();

  constructor(
    private modal: NgbModal,
    private upgradeModal: UpgradeModalService,
    private inboxAutomator: InboxAutomatorService,
    private organizationService: OrganizationService
  ) { }

  open(options: InboxAutomatorModalOptions): Promise<InboxAutomatorModalResult> {
    const key = (options?.email || '').trim().toLowerCase();
    if (!key) { return Promise.resolve(null); }
    if (this.open$.has(key)) { return this.open$.get(key); }

    const result = this.run(options).finally(() => this.open$.delete(key));
    this.open$.set(key, result);
    return result;
  }

  private async run(options: InboxAutomatorModalOptions): Promise<InboxAutomatorModalResult> {
    let current = options;
    for (;;) {
      const result = await this.show(current);
      if (result !== 'upgrade') { return result; }

      const bought = await this.upgradeModal.open({
        reason: 'inbox-automator',
        requiredFeature: MODULES.INBOX_AUTOMATOR,
        featureName: 'Inbox Automator'
      });
      if (bought) {
        // Every mailbox pill on the page reloads too, now the plan includes it.
        this.inboxAutomator.changed$.next(this.organizationService.selectedOrganization);
      }
      current = { ...current, justConnected: false, justUpgraded: bought };
    }
  }

  private show(options: InboxAutomatorModalOptions): Promise<InboxAutomatorModalResult> {
    const ref = this.modal.open(InboxAutomatorModalComponent, {
      centered: true,
      windowClass: 'synced-dialog synced-dialog--form synced-dialog--elevated',
      backdropClass: 'synced-dialog-backdrop synced-dialog-backdrop--elevated'
    });
    ref.componentInstance.options = options;
    return ref.result.then(r => (r || null) as InboxAutomatorModalResult, () => ref.componentInstance?.result || null);
  }

  /**
   * For a mailbox-connected postMessage. Only Gmail and Outlook have Inbox Automator, and a
   * message without an address (an older API) cannot say which mailbox to offer it for.
   */
  openForConnected(message: any): void {
    const provider = String(message?.provider || '');
    const email = typeof message?.email === 'string' ? message.email.trim() : '';
    if (!email || !/^(gmail|outlook)$/i.test(provider)) { return; }
    // A mailbox that saved but cannot receive yet gets its warning toast, not an upsell.
    if (typeof message?.warning === 'string' && message.warning.trim()) { return; }
    this.open({ email, provider, justConnected: true });
  }
}
